'use client';

import { LogoRegister, LogoSlogan, LogoText } from '@/components/common';
import { LogoLinks } from '@/components/common/smartguy-logo';
import AppFooter from '@/components/layouts/AppFooter';
import CommonNav from '@/components/layouts/CommonNav';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
} from '@/components/ui/pagination';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { stripHtml } from '@/lib/strip-html';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import debounce from 'lodash.debounce';
import { X } from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import { ChevronsLeft } from 'lucide-react';
import { ChevronLeft } from 'lucide-react';
import { ChevronRight } from 'lucide-react';
import { ChevronsRight } from 'lucide-react';
import { Clock } from 'lucide-react';
import { Search } from 'lucide-react';
import { Calendar } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';

export default function Blogs({
  categories,
  data,
  categoryId,
  categorySlug,
  initialSearchParams = {},
}) {
  const router = useRouter();

  const [openDropdown, setOpenDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const createUrl = (page) => {
    const params = new URLSearchParams();
    if (initialSearchParams.category) {
      params.set('category', initialSearchParams.category);
    }
    if (page > 1) {
      params.set('page', String(page));
    }
    const qs = params.toString();
    return qs ? `/blogs?${qs}` : '/blogs';
  };

  const searchBlogs = async (query) => {
    const url = `${process.env.NEXT_PUBLIC_API_URL}/blogs/list?query=${query}&${categoryId ? `blogCategory=${categoryId}&` : ''}`;

    try {
      const res = await fetch(url);

      if (!res.ok) {
        // return null
      } else {
        const result = await res.json();
        return result;
      }
    } catch (error) {
      return [];
      return null;
    }
  };

  const MIN_SEARCH_LENGTH = 3;

  const debouncedSearchVideos = useCallback(
    debounce(async (query) => {
      if (!query.trim() || query.trim().length < MIN_SEARCH_LENGTH) {
        setResult([]);
        setSearching(false);
        return;
      }

      try {
        const data = await searchBlogs(query);
        setResult(Array.isArray(data?.data) ? data?.data : []);
      } catch {
        setResult([]);
      } finally {
        setSearching(false);
      }
    }, 500),
    []
  );

  const handleInputChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query.length >= MIN_SEARCH_LENGTH) {
      setSearching(true);
      setOpenDropdown(true);
      debouncedSearchVideos(query);
    } else if (query.length >= 1) {
      setResult([]);
      setSearching(false);
      setOpenDropdown(true);
    } else {
      setResult([]);
      setSearching(false);
      setOpenDropdown(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;

    const months = Math.floor(days / 30);
    if (months < 12) {
      if (months === 1) return '1 month ago';
      return `${months} months ago`;
    }

    // For dates older than 1 year, you can choose either format:
    // Option 1: Return "X years ago"
    const years = Math.floor(months / 12);
    if (years === 1) return '1 year ago';
    return `${years} years ago`;

    // Option 2: Return formatted date string for very old dates
    // return date.toLocaleDateString();
  };

  const formatViews = (number) => {
    if (number < 1000) return number.toString();
    if (number < 1_000_000) return (number / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    if (number < 1_000_000_000) return (number / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    return (number / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
  };

  const handleOptionSelect = (link) => {
    router.push(link);
  };

  const handleReset = async () => {
    router.push('/blogs');
  };

  return (
    <>
      <div className="bg-white">
        <div className="flex flex-col items-center justify-center">
          <Link href="/" className={`flex flex-col items-center justify-center`}>
            <div className="flex">
              <LogoText className="text-3xl" />
              <LogoRegister />
            </div>
            <LogoSlogan className="me-4 text-center text-[.7rem]" />
          </Link>
          <LogoLinks />
        </div>
        <div className="">
          <CommonNav />
        </div>
      </div>

      <main className="flex min-h-screen w-full flex-col items-center overflow-y-auto bg-white p-2">
        <div className="container mx-auto space-y-4">
          <div className="mx-auto w-full max-w-xs sm:max-w-md">
            <div className="relative mt-3 mb-6 w-full">
              <div className="group relative flex items-center transition-all duration-300 hover:scale-[1.01]">
                <Search
                  className="text-primary absolute top-1/2 left-4 z-20 h-4 w-4 -translate-y-1/2 transform transition-colors duration-300"
                  aria-hidden="true"
                />
                <Input
                  type="text"
                  placeholder={categoryId ? 'Search News topics…' : 'Search Blogs topics…'}
                  className="focus-visible:ring-dark-blue z-10 w-full max-w-lg rounded-full py-6 pr-12 pl-11 text-lg focus-visible:ring-1"
                  onChange={handleInputChange}
                />
              </div>

              <Popover open={openDropdown} onOpenChange={setOpenDropdown}>
                <PopoverTrigger asChild>
                  <div />
                </PopoverTrigger>

                <PopoverContent
                  align="start"
                  sideOffset={8}
                  className="mx-auto w-xs rounded-sm border-0 bg-white p-0 shadow-lg sm:w-[420px]"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                >
                  <ScrollArea
                    className={`${result.length <= 5 ? 'h-max' : 'h-[400px] sm:h-[400px]'}`}
                  >
                    <>
                      {searchQuery.length >= MIN_SEARCH_LENGTH &&
                        !searching &&
                        result.length > 0 &&
                        result.map((blog, index) => (
                          <div
                            key={index}
                            className="text-primary flex cursor-pointer justify-start gap-2 px-4 py-2 hover:bg-gray-200"
                            onClick={() => handleOptionSelect(`/blogs/${blog?.slug}`)}
                          >
                            {blog.imageUrl && (
                              <div className="relative h-[80px] w-[120px] flex-shrink-0 overflow-hidden rounded-[4px]">
                                <Image
                                  src={`${process.env.NEXT_PUBLIC_S3_BUCKET_URL}/${blog.imageUrl}`}
                                  width={130}
                                  height={80}
                                  alt=""
                                  priority="lazy"
                                  className="h-full w-full object-cover"
                                />
                              </div>
                            )}

                            <div className="flex flex-1 flex-col gap-1">
                              <div
                                className="line-clamp-2 text-[.9rem] font-semibold"
                                style={{ lineHeight: '18px' }}
                              >
                                {blog.title}
                              </div>
                              <div className="text-[.8rem] italic">{blog?.blogCategory?.title}</div>
                              <div className="text-[.65rem]">
                                <span className="me-2 text-blue-800">
                                  {formatViews(blog.views)} views.{' '}
                                </span>
                                <span>{formatTime(blog.createdAt)}</span>
                              </div>
                            </div>
                          </div>
                        ))}

                      {searchQuery.length >= 1 &&
                        searchQuery.length < MIN_SEARCH_LENGTH &&
                        !searching && (
                          <p className="text-muted-foreground px-4 py-2 text-sm">
                            Type at least 3 characters to search.
                          </p>
                        )}

                      {searchQuery.length >= MIN_SEARCH_LENGTH &&
                        !searching &&
                        result.length === 0 && (
                          <p className="px-4 py-2">
                            No blogs found. Try searching with different keywords.
                          </p>
                        )}

                      {searchQuery.length >= MIN_SEARCH_LENGTH && searching && (
                        <div className="space-y-3 px-4 py-2">
                          {[...Array(4)].map((_, i) => (
                            <div key={i} className="flex gap-3">
                              <div className="h-20 w-[130px] animate-pulse rounded-sm bg-gray-200" />

                              {/* Text skeleton */}
                              <div className="flex-1 space-y-2">
                                <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
                                <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
                                <div className="h-3 w-1/2 animate-pulse rounded bg-gray-200" />
                                <div className="mt-2 h-2 w-1/3 animate-pulse rounded bg-gray-200" />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  </ScrollArea>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {!categoryId && (
            <div className="flex flex-wrap gap-2">
              {categories &&
                categories.length > 0 &&
                categories.map((item, index) => (
                  <div key={index}>
                    <Link href={`/blogs?category=${item.slug}`}>
                      <Button
                        variant="outline"
                        className={cn(
                          'h-auto w-auto max-w-full cursor-pointer px-2 py-2 text-xs sm:py-3 md:text-sm',
                          'border-dark-blue text-dark-blue hover:bg-periwinkle hover:text-dark-blue hover:border-periwinkle rounded-full text-center break-words',
                          categorySlug === item.slug && 'bg-periwinkle text-dark-blue border-0'
                        )}
                      >
                        {item.title}
                      </Button>
                    </Link>
                  </div>
                ))}

              <Button
                variant="icon"
                onClick={() => handleReset()}
                className="border-dark-blue hover:bg-periwinkle hover:text-dark-blue hover:border-periwinkle h-8 w-8 cursor-pointer rounded-full border sm:h-11 sm:w-11"
              >
                <X className="text-primary size-5" />
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {data?.data &&
              data?.data.length > 0 &&
              data?.data.map((blog, index) => (
                <Card
                  key={index}
                  className="group col-span-1 w-full gap-1 p-0 pb-3 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                >
                  <CardHeader className="p-0">
                    <Link href={`/blogs/${blog.slug}`}>
                      <div className="relative h-[220px] w-full overflow-hidden rounded-t-lg">
                        <Image
                          src={`${process.env.NEXT_PUBLIC_S3_BUCKET_URL}/${blog.imageUrl}`}
                          alt={blog.title}
                          fill
                          className="object-cover"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        />
                      </div>
                    </Link>
                  </CardHeader>

                  <CardContent>
                    <div className="text-muted-foreground mb-3 flex w-full items-center justify-between gap-4 text-sm">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {blog?.createdAt && format(new Date(blog.createdAt), 'dd MMM yyyy')}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        {blog.readTime && (
                          <>
                            <Clock className="h-4 w-4" />
                            <span>{blog.readTime} min read</span>
                          </>
                        )}
                      </div>
                    </div>

                    <CardTitle className="group-hover:text-primary line-clamp-2 text-xl font-bold transition-colors">
                      <Link href={`/blogs/${blog.slug}`}>{blog.title}</Link>
                    </CardTitle>

                    <CardDescription className="mt-2 line-clamp-3">
                      {stripHtml(blog.description)}
                    </CardDescription>
                  </CardContent>

                  <CardFooter className="flex items-center justify-end pt-2">
                    <Link
                      href={`/blogs/${blog.slug}`}
                      className="hover:text-primary flex items-center gap-1 text-sm font-semibold"
                    >
                      Read more<span className="sr-only">{blog.title}</span>
                      <ArrowRight className="relative top-px h-4 w-4" />
                    </Link>
                  </CardFooter>
                </Card>
              ))}
          </div>

          <div className="mt-5 block">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <Button
                    variant="ghost"
                    className="flex w-auto px-2 disabled:cursor-not-allowed"
                    disabled={data.meta.firstPage === data.meta.page}
                  >
                    <Link
                      href={createUrl(data.meta.firstPage)}
                      className="text-md flex items-center gap-1 text-gray-600"
                    >
                      <ChevronsLeft /> First
                    </Link>
                  </Button>
                </PaginationItem>

                <PaginationItem>
                  <Button
                    variant="ghost"
                    className="flex w-auto px-2 disabled:cursor-not-allowed"
                    disabled={!data.meta.hasPreviousPage}
                  >
                    <Link
                      href={createUrl(data.meta.page - 1)}
                      className="text-md flex items-center gap-1 text-gray-600"
                    >
                      <ChevronLeft /> Previous
                    </Link>
                  </Button>
                </PaginationItem>

                <PaginationItem>
                  <PaginationLink href="#" isActive>
                    {data.meta.page}
                  </PaginationLink>
                </PaginationItem>

                <PaginationItem>
                  <Button
                    variant="ghost"
                    className="flex w-auto px-2 disabled:cursor-not-allowed"
                    disabled={!data.meta.hasNextPage}
                  >
                    <Link
                      href={createUrl(data.meta.page + 1)}
                      className="text-md flex items-center gap-1 text-gray-600"
                    >
                      Next <ChevronRight />
                    </Link>
                  </Button>
                </PaginationItem>

                <PaginationItem>
                  <Button
                    variant="ghost"
                    className="flex w-auto px-2 disabled:cursor-not-allowed"
                    disabled={data.meta.lastPage === data.meta.page}
                  >
                    <Link
                      href={createUrl(data.meta.lastPage)}
                      className="text-md flex items-center gap-1 text-gray-600"
                    >
                      Last <ChevronsRight />
                    </Link>
                  </Button>
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      </main>

      <AppFooter />
    </>
  );
}
